// T4: Amenity reservation calendar.
//
// Covers Stitch v2 design `amenity_calendar_reservations`. Single
// component used at two routes:
//   /admin/amenities/reservations  — operator scope (cancel any)
//   /me/amenities/reservations     — resident scope (cancel own)
//
// The scope prop drives:
//   - which endpoints we call (/amenities vs /me/amenities)
//   - whether the cancel button is shown for not-owned rows

import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Badge, Button, Card, CardBody, Icon, Spinner } from '@ng-unifi/ui';
import type { AmenityReservation } from '@ng-unifi/domain';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';
import { useAuth } from '../auth/AuthContext.js';
import { amenityReservationStatusLabel } from '../lib/humanLabels.js';

interface AmenityLite {
  id: string;
  name: string;
}

interface ListResponse<T> {
  data: T[];
}

interface Props {
  /** 'operator' = master/tenant_admin/tenant_manager via TENANT_MANAGE_AMENITIES.
   *  'resident' = self-service via RESIDENT_SELF_SERVICE. */
  scope: 'operator' | 'resident';
}

function toDatetimeLocal(d: Date): string {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export function AmenityReservationsPage({ scope }: Props) {
  const { state } = useAuth();
  const [amenities, setAmenities] = useState<AmenityLite[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [rows, setRows] = useState<AmenityReservation[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<AmenityReservation | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  const userId = state.status === 'authed' ? state.user.id : null;

  // Amenity list. Residents use /me/amenities because /amenities is
  // tenant-admin scoped.
  useEffect(() => {
    const amenityListUrl = scope === 'resident' ? '/api/v1/me/amenities' : '/api/v1/amenities';
    apiClient
      .get<ListResponse<AmenityLite>>(amenityListUrl)
      .then((r) => {
        setAmenities(r.data);
        if (r.data.length > 0) setSelectedId((prev) => prev || r.data[0]!.id);
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Failed to load amenities'));
  }, [scope]);

  // Reservations for the selected amenity (operator) or for the
  // calling resident (resident).
  useEffect(() => {
    let cancelled = false;
    setRows(null);
    setError(null);
    const url =
      scope === 'operator'
        ? selectedId
          ? `/api/v1/amenities/${selectedId}/reservations`
          : null
        : '/api/v1/me/amenities/reservations';
    if (!url) {
      setRows([]);
      return;
    }
    apiClient
      .get<ListResponse<AmenityReservation>>(url)
      .then((r) => {
        if (cancelled) return;
        setRows(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : 'Failed to load reservations');
      });
    return () => {
      cancelled = true;
    };
  }, [scope, selectedId, reloadTick]);

  const filtered = useMemo(
    () =>
      scope === 'resident' && selectedId
        ? (rows ?? []).filter((r) => r.amenity_id === selectedId)
        : rows ?? [],
    [rows, scope, selectedId],
  );

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">
            {scope === 'operator' ? 'Amenity reservations' : 'My reservations'}
          </h1>
          <p className="text-on-surface-variant">
            {scope === 'operator'
              ? 'Manage bookings across every amenity.'
              : 'Book and cancel your own amenity reservations.'}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} disabled={!selectedId}>
          <Icon name="add" /> {scope === 'operator' ? 'Book on behalf' : 'Reserve'}
        </Button>
      </header>

      {error ? (
        <p role="alert" className="text-error">
          <Icon name="error" /> {error}
        </p>
      ) : null}

      <Card>
        <CardBody>
          <label className="grid gap-1 max-w-sm">
            <span className="text-sm font-medium">Amenity</span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="rounded border border-outline px-3 py-2"
              disabled={amenities.length === 0}
            >
              {amenities.length === 0 ? <option value="">No amenities</option> : null}
              {amenities.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </select>
          </label>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          {rows === null ? (
            <Spinner />
          ) : filtered.length === 0 ? (
            <p className="py-3 text-on-surface-variant">No reservations.</p>
          ) : (
            <ul className="divide-y divide-outline-variant">
              {filtered.map((r) => {
                const ownsThis = r.reserved_by_user_id === userId;
                const canCancel =
                  r.status === 'booked' && (scope === 'operator' || ownsThis);
                return (
                  <li key={r.id} className="flex items-center gap-3 py-3">
                    <Badge tone={r.status === 'booked' ? 'success' : 'neutral'}>
                      {amenityReservationStatusLabel(r.status)}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">
                        {new Date(r.starts_at).toLocaleString()} →{' '}
                        {new Date(r.ends_at).toLocaleTimeString()}
                      </p>
                      {r.notes ? (
                        <p className="text-sm text-on-surface-variant">{r.notes}</p>
                      ) : null}
                    </div>
                    {canCancel ? (
                      <Button variant="ghost" onClick={() => setCancelTarget(r)}>
                        Cancel
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </CardBody>
      </Card>

      {createOpen && selectedId ? (
        <ReserveModal
          scope={scope}
          amenityId={selectedId}
          onCancel={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            reload();
          }}
        />
      ) : null}

      {cancelTarget ? (
        <CancelModal
          scope={scope}
          row={cancelTarget}
          onCancel={() => setCancelTarget(null)}
          onDone={() => {
            setCancelTarget(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function ReserveModal({
  scope,
  amenityId,
  onCancel,
  onCreated,
}: {
  scope: 'operator' | 'resident';
  amenityId: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const now = new Date();
  const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
  const [startsAt, setStartsAt] = useState(toDatetimeLocal(now));
  const [endsAt, setEndsAt] = useState(toDatetimeLocal(oneHourLater));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idemKey] = useState(() => newIdempotencyKey());

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const url =
        scope === 'operator'
          ? `/api/v1/amenities/${amenityId}/reservations`
          : `/api/v1/me/amenities/${amenityId}/reservations`;
      await apiClient.withIdempotencyKey(idemKey).post(url, {
        starts_at: new Date(startsAt).toISOString(),
        ends_at: new Date(endsAt).toISOString(),
        notes: notes.trim() || undefined,
      });
      onCreated();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.code === 'conflict'
            ? 'That window is already reserved. Pick a different time.'
            : e.message
          : 'Failed to reserve';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reserve-title"
      className="fixed inset-0 bg-on-surface/40 flex items-center justify-center p-4 z-50"
    >
      <Card className="w-full max-w-md">
        <CardBody>
          <h2 id="reserve-title" className="text-lg font-semibold mb-3">
            Reserve amenity
          </h2>
          <form onSubmit={onSubmit} className="grid gap-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <label className="grid gap-1">
                <span>Starts</span>
                <input
                  type="datetime-local"
                  required
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                  className="rounded border border-outline px-3 py-2"
                />
              </label>
              <label className="grid gap-1">
                <span>Ends</span>
                <input
                  type="datetime-local"
                  required
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                  className="rounded border border-outline px-3 py-2"
                />
              </label>
            </div>
            <label className="grid gap-1">
              <span>Notes (optional)</span>
              <input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                maxLength={500}
                className="rounded border border-outline px-3 py-2"
              />
            </label>
            {error ? (
              <p role="alert" className="text-error">
                <Icon name="error" /> {error}
              </p>
            ) : null}
            <div className="flex gap-2 justify-end mt-2">
              <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Reserving…' : 'Reserve'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function CancelModal({
  scope,
  row,
  onCancel,
  onDone,
}: {
  scope: 'operator' | 'resident';
  row: AmenityReservation;
  onCancel: () => void;
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idemKey] = useState(() => newIdempotencyKey());

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const url =
        scope === 'operator'
          ? `/api/v1/amenities/${row.amenity_id}/reservations/${row.id}`
          : `/api/v1/me/amenities/reservations/${row.id}`;
      await apiClient
        .withIdempotencyKey(idemKey)
        .deleteWithBody(url, reason.trim() ? { reason: reason.trim() } : {});
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to cancel');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cancel-title"
      className="fixed inset-0 bg-on-surface/40 flex items-center justify-center p-4 z-50"
    >
      <Card className="w-full max-w-md">
        <CardBody>
          <h2 id="cancel-title" className="text-lg font-semibold mb-3">
            Cancel reservation
          </h2>
          <p className="text-sm text-on-surface-variant mb-3">
            {new Date(row.starts_at).toLocaleString()} → {new Date(row.ends_at).toLocaleTimeString()}
          </p>
          <form onSubmit={onSubmit} className="grid gap-3">
            <label className="grid gap-1">
              <span>Reason (optional)</span>
              <input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                className="rounded border border-outline px-3 py-2"
              />
            </label>
            {error ? (
              <p role="alert" className="text-error">
                <Icon name="error" /> {error}
              </p>
            ) : null}
            <div className="flex gap-2 justify-end mt-2">
              <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
                Keep
              </Button>
              <Button type="submit" variant="danger" disabled={submitting}>
                {submitting ? 'Cancelling…' : 'Cancel reservation'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
